'use client';
/**
 * LandingClickTracker — Client Component de delegação de eventos de clique.
 *
 * Usa event delegation via document.addEventListener para capturar cliques
 * em elementos que carregam `data-event` attributes (renderizados por Server Components).
 *
 * Eventos cobertos:
 *   - data-event="plan-card-clicked"  → trackPlanCardClicked
 *   - data-event="club-card-clicked"  → trackClubCardClicked
 *   - data-event="landing-cta"        → trackLandingCtaClicked
 *
 * Segue o padrão Server/Client boundary: os elementos clicáveis vivem no Server
 * Component como HTML estático com data-attributes; este componente os escuta.
 *
 * AGR-2026-012: colocado em apps/web/app/(public)/_components/.
 * AGR-2026-013: recebe baseProps do Server Component (sem fetch client-side).
 */
import * as React from 'react';
import {
  trackPlanCardClicked,
  trackClubCardClicked,
  trackLandingCtaClicked,
  type BaseLandingEventProps,
  type PlanKey,
} from '@brisa/shared/observability/landing';

interface LandingClickTrackerProps extends BaseLandingEventProps {}

export function LandingClickTracker(base: LandingClickTrackerProps) {
  React.useEffect(() => {
    const handler = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target) return;

      const el = target.closest<HTMLElement>('[data-event]');
      if (!el) return;

      const eventName = el.dataset.event;

      if (eventName === 'plan-card-clicked') {
        const planKey = el.dataset.planKey as PlanKey | undefined;
        const surface = el.dataset.surface ?? 'unknown';
        if (planKey) {
          trackPlanCardClicked(
            { plan_key: planKey, surface, cta_destination: 'waitlist' },
            base,
          );
        }
        return;
      }

      if (eventName === 'club-card-clicked') {
        const clubSlug = el.dataset.clubSlug ?? '';
        const clubId = el.dataset.clubId ?? clubSlug;
        const integrationStatus =
          (el.dataset.integrationStatus as 'listed' | 'partner') ?? 'listed';
        const surface = el.dataset.surface ?? 'unknown';
        trackClubCardClicked(
          { club_id: clubId, club_slug: clubSlug, integration_status: integrationStatus, surface },
          base,
        );
        return;
      }

      if (eventName === 'landing-cta') {
        const ctaLabel = el.dataset.ctaLabel ?? el.textContent?.trim() ?? '';
        const ctaDestination = 'waitlist' as const;
        const from = el.dataset.from ?? 'unknown';
        const surface = el.dataset.surface ?? 'unknown';
        const surfaceGranular = el.dataset.surfaceGranular ?? surface;
        trackLandingCtaClicked(
          { cta_label: ctaLabel, cta_destination: ctaDestination, from, surface, surface_granular: surfaceGranular },
          base,
        );
        return;
      }
    };

    document.addEventListener('click', handler, { capture: false });
    return () => {
      document.removeEventListener('click', handler, { capture: false });
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
}
